import moment from 'moment';
import { defineMessages } from 'react-intl';
import {
  PREPARER, SENDER, getBoundingBox,
} from 'hellospa/components/signature-request';
import uuid from 'uuid/v4';
import createDebug from 'debug';
import {
  // I'd like to have EditorContext pass this instead, but its tests prevent
  // wrapping it in injectIntl.
  intl,
} from 'hellospa/components/hs-intl-provider';
import { trackHeapCustomEvent } from 'common/utils/heap';
import {
  validationOptions, hasFeature, dateFormats, getMinFieldDimensions,
  FEATURE_EMBED, autofillFieldsTitle, FEATURE_QES,
} from './constants';
import { DEFAULT_FONT_FAMILY } from '../signature-request/constants';
// eslint-disable-next-line no-unused-vars
import { AutofillFieldTypes, Field } from './types';

const debug = createDebug('hs-editor:field-operations');

// exported for tests
export const messages = defineMessages({
  multiTypeMergeFieldError: {
    description: 'error message in editor asking user to make merge field label name unique across all components',
    defaultMessage: 'Different component types cannot share the same merge field name. Please make sure they are unique across component types.',
  },

  invalidRegex: {
    description: 'error message in editor asking user to fix their custom regular expression',
    defaultMessage: 'Invalid RegEx: {validationCustomRegex}',
  },
  formFieldValidationError: {
    description: 'error message in editor asking user to make all fields label unique',
    defaultMessage: 'Please create a unique Field Label for all fields you are completing when sending the template out',
  },
  duplicateApiIdValidationError: {
    description: 'error message in editor asking user to make unique Api ID for all fields',
    defaultMessage: 'Please create a unique Api Id for all fields you are completing when sending the template out',
  },
  noDropdownOptions: {
    description: 'error message in editor asking user to add at least one option to user\'s dropdown',
    defaultMessage: 'Please add at least one option to your dropdown',
  },
  noComponents: {
    description: 'error message in editor asking user to add at least one field to the page like signature fields, dropdown',
    defaultMessage: 'Add a field from the left column',
  },
  prepareFieldForYourself: {
    id: '',
    description: 'error message in editor asking user to add at least one non-date field to complete the request',
    defaultMessage: 'Add a field that is not a date',
  },
  prepareFieldForSigner: {
    id: '',
    description: 'error message in editor asking user to add at least one non-date field to complete the request',
    defaultMessage: 'Add a field that is not a date for {name}',
  },
  signerOrderFields: {
    description: 'error message in editor asking user to assign each user a field to complete for signature request',
    defaultMessage: 'The current order of signers makes you signer #{signerOrder}, but you have not assigned any fields to be completed in the future by {name}.',
  },
  formURLFieldValidationError: {
    description: 'error message in editor stating the url inputted by user is not valid',
    defaultMessage: 'Invalid URL. URL must start with http:// or https://, and cannot point to a local URL (eg: localhost, 127.0.0.1)',
  },
  elementsOverlap: {
    description: 'popup message in editor, warning user that by moving forward with the action elements in editor will overlap',
    defaultMessage: 'If you align this way, your elements will overlap.',
  },
  noSignFieldForSigner: {
    id: '',
    description: 'error message in editor asking user to add at least one signature field to complete the request',
    defaultMessage: 'Assign at least one signature field to {name}',
  },
  fieldTypeSignature: {
    description: 'Signature field type',
    defaultMessage: 'Signature',
  },
  fieldTypeInitial: {
    description: 'Initial field type',
    defaultMessage: 'Initial',
  },
  fieldTypeDateSigned: {
    description: 'Date Signed field type',
    defaultMessage: 'DateSigned',
  },
  fieldTypeTextbox: {
    description: 'Textbox field type',
    defaultMessage: 'Textbox',
  },
  fieldTypeCheckbox: {
    description: 'Text describing what kind of type this field is.',
    defaultMessage: 'Checkbox',
  },
  fieldTypeRadioItem: {
    description: 'RadioItem field type',
    defaultMessage: 'RadioItem',
  },
  fieldTypeItemGroup: {
    description: 'Item field type which are placeholders for self generated input field for eg: RadiobuttonGroup, CheckBoxGroup,',
    defaultMessage: '{fieldTypeItem}Group',
  },
  fieldTypeDropdown: {
    description: 'Text describing what kind of type this field is.',
    defaultMessage: 'Dropdown',
  },
  fieldTypeHyperlink: {
    description: 'Hyperlink field type',
    defaultMessage: 'Hyperlink',
  },
  groupLabel: {
    description: 'Group Label',
    defaultMessage: 'Group',
  },
  fullNameLabel: {
    description: 'editor, field label, full name label',
    defaultMessage: 'FullName',
  },
  firstNameLabel: {
    description: 'editor, field label, first name label',
    defaultMessage: 'FirstName',
  },
  lastNameLabel: {
    description: 'editor, field label, last name label',
    defaultMessage: 'LastName',
  },
  emailLabel: {
    description: 'editor, field label, email label',
    defaultMessage: 'Email',
  },
  companyLabel: {
    description: 'editor, field label, company label',
    defaultMessage: 'Company',
  },
  titleLabel: {
    description: 'editor, field label, title label',
    defaultMessage: 'Title',
  },
});

// Nothing outside of this file should ever need these values
const DEFAULT_TEXTBOX_WIDTH = 72;
const DEFAULT_TEXTBOX_HEIGHT = 12;

// Freezing the object prevents anyone from mutating the object. For
// undo/redo to work, the object can never change. it MUST  get replaced.
// bad: fieldData.foo = "newFoo"
// good: fieldData = { ...fieldData, foo: "newFoo" }
const freeze = (obj) => {
  // I'm only freezing in development so that it crashes, forcing the developer
  // to fix the problem immediately. But I don't want to accidentally crash
  // anyone on production.
  if (process.env.NODE_ENV !== 'production') {
    Object.freeze(obj);
  }
  return obj;
};

const peek = (array) => (array.length > 0
  ? array[array.length - 1]
  : null);

export const intersectY = (a, b) => (
  a.y < b.y + b.height
  && a.y + a.height > b.y
);
export const intersectX = (a, b) => (
  a.x < b.x + b.width
  && a.x + a.width > b.x
);

// https://developer.mozilla.org/en-US/docs/Games/Techniques/2D_collision_detection
export const intersect = (a, b) => (
  intersectY(a, b) && intersectX(a, b)
);

const anyIntersect = (items) => {
  for (let current = 0; current < items.length; current++) {
    for (let next = current + 1; next < items.length; next++) {
      if (intersect(items[current], items[next])) {
        return true;
      }
    }
  }
  return false;
};

const getCenter = (fieldData) => ({
  x: fieldData.x + (fieldData.width / 2),
  y: fieldData.y + (fieldData.height / 2),
});

const measureDistance = (a, b) => {
  const centerA = getCenter(a);
  const centerB = getCenter(b);

  return Math.sqrt(
    ((centerA.x - centerB.x) ** 2)
    + ((centerA.y - centerB.y) ** 2),
  );
};

// Check if A completely encloses B
const encloses = (a, b) => (
  a.x <= b.x
  && a.y <= b.y
  && (a.x + a.width) >= (b.x + b.width)
  && (a.y + a.height) >= (b.y + b.height)
);

// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort
//
// This is attempting to sort left to right, top to bottom. If two fields are
// next to each other, but the right field is slightly higher, it should NOT be
// sorted higher.
const fieldSorter = (a, b) => {
  // This function takes advantage of JavaScript's falsy behavior. Any negative
  // number or positive number is truthy, and 0 is falsy
  const pageDiff = a.pageIndex - b.pageIndex;
  if (pageDiff !== 0) return pageDiff;

  if (intersectY(a, b)) {
    return a.x - b.x;
  }

  return a.y - b.y;
};

const baseField = {
  pageIndex: 0,
  name: '',
  x: 0,
  y: 0,
  required: true,
  linkId: null,
  autoFillType: null,
};

export default function fieldOperations({
  pages,
  signers,
  signerOrder,
  mode = 0,
  currentRoleId = PREPARER,
  dateFormat = Object.keys(dateFormats)[0],
  embeddedMergeFields = [],
  accountDateFormat,
  preparer,
}) {
  const generateId = () => uuid();
  let defaultSignature = null;
  let defaultInitials = null;

  // This is the signer that will be used by the next field you create and it is
  // set by the last field you changed.
  let defaultSigner = null;
  // When defaultSigner is not valid for a field, it will go through history to
  // find a valid signer.
  let signerHistory = [];
  function findSignerFromHistory(exclude) {
    for (let i = signerHistory.length - 1; i >= 0; i--) {
      const signer = signerHistory[i];
      if (!exclude.includes(signer)) {
        return signer;
      }
    }
  }

  let signerIds = [];

  const setSigners = (signersObj) => {
    // eslint-disable-next-line no-param-reassign
    signers = signersObj.map((s) => ({
      ...s,
      id: String(s.id),
    }));
    signerIds = signers.map((s) => s.id).sort();
    // signers is sorted, use first signer as default signer on init
    const firstSigner = signers.find((signerObj) => {
      return signerObj.order != null && signerObj.order >= 0;
    });

    // Remove any signers from history that were deleted
    signerHistory = signerHistory.filter(
      (id) => signerIds.includes(id),
    );

    // Always put the first signer at the start of history as a fallback for any
    // field.
    signerHistory.unshift(
      firstSigner ? firstSigner.id : signerIds[0],
    );

    // if default signer is deleted, then move on and figure out the next signer ID
    // otherwise leave it as it is
    if (!signerIds.includes(defaultSigner)) {
      defaultSigner = signerHistory[signerHistory.length - 1];
    }
  };

  const setPages = (pagesObj) => {
    // eslint-disable-next-line no-param-reassign
    pages = pagesObj;
  };

  setSigners(signers);

  const defaultFields = {
    signature: {
      ...baseField,
      type: 'signature',
      width: 108,
      height: 27,
    },
    initials: {
      ...baseField,
      type: 'initials',
      width: 72,
      height: 27,
    },
    text: {
      ...baseField,
      type: 'text',
      width: DEFAULT_TEXTBOX_WIDTH,
      // TextField.applyFieldConstraints() says the 12pt font is 11 tall. I
      // don't know why it isn't 12
      height: DEFAULT_TEXTBOX_HEIGHT,
      lineHeight: DEFAULT_TEXTBOX_HEIGHT,
      masked: false,
    },
    dropdown: {
      ...baseField,
      type: 'dropdown',
      width: 72,
      height: DEFAULT_TEXTBOX_HEIGHT,
      options: [],
    },
    radiobutton: {
      ...baseField,
      type: 'radiobutton',
      width: 18,
      height: 18,
      // Because radio buttons are a group, they need to be managed using
      // requirement
      required: false,
      requirement: 'require_0-1',
    },
    checkbox: {
      ...baseField,
      type: 'checkbox',
      width: 18,
      height: 18,
      required: false,
    },
    date: {
      ...baseField,
      type: 'date',
      width: 80,
      height: 18,
      required: true,
      dateFormat,
    },
    hyperlink: {
      ...baseField,
      type: 'hyperlink',
      width: DEFAULT_TEXTBOX_WIDTH,
      height: DEFAULT_TEXTBOX_HEIGHT,
      lineHeight: DEFAULT_TEXTBOX_HEIGHT,
      required: false,
    },
  };

  const originalDefaults = { ...defaultFields };
  function getOriginalDefaultSize(fieldType, fieldDimension) {
    // Return default (18) in case no fieldType or fieldDimension is not defined.
    // i,e. Radios, dropdown, etc.
    if (
      originalDefaults[fieldType] == null
      || originalDefaults[fieldType][fieldDimension] == null
    ) {
      return 18;
    }

    return originalDefaults[fieldType][fieldDimension];
  }


  function getDefaultSignature(type) {
    return (type === 'signature') ? defaultSignature : defaultInitials;
  }

  function setDefaultSignature(field) {
    if (field.signer === PREPARER && (field.type === 'signature' || field.type === 'initials')) {
      const defaultSignature = getDefaultSignature(field.type);
      if (field.signature == null && defaultSignature != null) {
        return { ...field, signature: defaultSignature };
      }
    }

    return field;
  }

  function setDefaultSigner(signerId) {
    signerHistory.push(defaultSigner);

    if (signerHistory.length > signers.length * 2) {
      const unique = signerHistory
        .reverse()
        .reduce((newHistory, value) => {
          if (newHistory.indexOf(value) === -1) {
            newHistory.push(value);
          }
          return newHistory;
        }, []);
      signerHistory = unique.reverse();
    }

    defaultSigner = signerId;
  }

  function buildFieldUpdate(fieldData, updates) {
    // Filter out any "updates" that don't actually change the field data.
    // eslint-disable-next-line no-param-reassign
    updates = Object.keys(updates).reduce((up, key) => {
      if (updates[key] !== fieldData[key]) {
        up[key] = updates[key];
      }
      return up;
    }, {});
    const data = { ...fieldData, ...updates };
    const extra = {};

    const setAutofillFieldValue = (autoFillType) => {
      if (autoFillType === 'name') {
        extra.value = `${preparer.firstName} ${preparer.lastName}`;
      } else {
        extra.value = preparer[autoFillType];
      }
    };

    if (updates.required && fieldData.group != null && fieldData.type === 'checkbox') {
      extra.required = false;
    }

    if (updates.requirement != null) {
      // This converts the empty string to null
      const value = updates.requirement || null;
      // If we have a value, set the group, otherwise clear the group
      extra.group = (value
        ? data.group || Math.random().toString(16).slice(2)
        : null
      );
      extra.required = false;
    }

    if (updates.width != null) {
      const minWidth = getMinFieldDimensions(data.type, 'width');
      if (data.width < minWidth) {
        extra.width = minWidth;
      }
    }
    if (updates.height != null) {
      const minHeight = getMinFieldDimensions(data.type, 'height');
      if (data.height < minHeight) {
        extra.height = minHeight;
      }
    }


    if (updates.validationType != null) {
      const value = updates.validationType;
      extra.validationCustomRegex = (value === 'custom_regex' ? '' : undefined);
      extra.validationCustomRegexFormatLabel = (value === 'custom_regex' ? '' : undefined);


      if (
        // If the field has a name and it's not the default
        fieldData.placeholder
        && fieldData.placeholder !== validationOptions[fieldData.validationType]
      ) {
        // then keep the existing name
      } else if (
        value === 'None'
        || value === 'numbers_only'
        || value === 'letters_only'
      ) {
        extra.placeholder = '';
      } else {
        extra.placeholder = validationOptions[value];
      }
    }

    if (data.type === 'text' && updates.autoFillType) {
      // user cannot change the placeholder, so set it to the type by default
      // moved this here since the Name type has 3 different types
      extra.placeholder = autofillFieldsTitle[updates.autoFillType];
    }

    // autofill fields can't be assigned to a sender
    if (data.autoFillType != null && data.signer === SENDER) {
      extra.signer = findSignerFromHistory([SENDER]);
    }
    if (updates.signer) {
      // If changing the signer of a checkbox, make sure preparer boxes are always
      // checked and non-preparers are automatically unchecked.
      if (data.type === 'checkbox' && fieldData.signer !== updates.signer) {
        if (updates.signer === PREPARER) {
          extra.checked = true;
        } else if (updates.signer !== PREPARER) {
          extra.checked = false;
        }
      }

      // if changing the signer of a textbox to the preparer and the textbox is masked
      // change the value of masked for the textbox to false
      if (
        data.type === 'text'
        && fieldData.signer !== updates.signer
        && (updates.signer === PREPARER || updates.signer === SENDER)
        && fieldData.masked === true
      ) {
        extra.masked = false;
      }

      // When changing the signer from PREPARER, reset any signature/value the
      // field might contain.
      if (fieldData.signer === PREPARER && fieldData.signer !== data.signer) {
        if (data.signature && updates.signature === undefined) {
          extra.signature = undefined;
        }
        if (data.value) {
          extra.value = undefined;
        }
      }

      // if the original signer was sender and it was changed to
      // a different signer remove the label from the field
      if (fieldData.signer === SENDER && fieldData.signer !== data.signer) {
        extra.name = undefined;
      }

      if (
        (data.type === 'signature' || data.type === 'initials')
        && updates.signer === PREPARER
        && updates.signature == null
      ) {
        const defaultSignature = getDefaultSignature(data.type);
        if (defaultSignature != null) {
          extra.signature = defaultSignature;
        } else {
          extra.open = true;
        }
      }
    }
    // if it's an auto fillable field assigned to the preparer, inject the preparer's data
    if (data.autoFillType && data.signer === PREPARER) {
      if (updates.autoFillType || updates.signer) {
        setAutofillFieldValue(data.autoFillType);
      }
    }

    // for embedded templates, if the signer is set to the SENDER
    // set the first merge field if available for the same type
    // and if label is being updated, make sure the field is
    // assigned to the SENDER as well, so we only update
    // SENDER fields within this block
    if (
      (updates.signer === SENDER || (fieldData.signer === SENDER && updates.name != null))
      && hasFeature(mode, FEATURE_EMBED)
      // make sure the textbox isn't an autofillable field
      && (data.type === 'checkbox' || (data.type === 'text' && data.autoFillType == null))
    ) {
      // get the names of all of the merge fields for this type
      // so we can check if an overridden label isn't used
      // when the signer is changed back to a SENDER
      const mergeFieldNames = embeddedMergeFields
        .filter((f) => f.type === data.type)
        .map((f) => f.name);

      const firstMergeField = mergeFieldNames[0];
      // make sure the existing label is part of the list of merge fields
      if (firstMergeField && !mergeFieldNames.includes(data.name)) {
        extra.name = firstMergeField;
      } else if (!firstMergeField) {
        // There are no merge fields for this type. The signer can't be SENDER
        extra.signer = signerHistory[signerHistory.length - 1];
      }
    }

    if (updates.signer || updates.dateFormat) {
      if (data.type === 'date') {
        if (updates.signer === PREPARER) {
          let dateFormat = data.dateFormat || Object.keys(dateFormats)[0];
          if (dateFormat === 'default') {
            dateFormat = accountDateFormat;
          }
          extra.value = moment().format(dateFormat);
        } else if (data.signer === PREPARER && updates.dateFormat) {
          const dateFormat = updates.dateFormat === 'default'
            ? accountDateFormat
            : updates.dateFormat;
          extra.value = moment().format(dateFormat);
        } else {
          extra.value = null;
        }
      }
    }

    if (data.type === 'dropdown' && updates.options && data.value) {
      // If the value got removed or changed, the default needs to reset
      if (updates.options.indexOf(data.value) === -1) {
        extra.value = undefined;
      }
    }

    if (data.type === 'hyperlink' && updates.signer && updates.signer !== PREPARER) {
      // hyperlink fields can only be assigned to PREPARER
      extra.type = 'text';

      if (data.name) {
        // eslint-disable-next-line no-use-before-define
        if (isDefaultFieldName(data.type, data.name, false)) {
          extra.name = undefined;
        }
      }
    }

    return { ...updates, ...extra };
  }

  const getDefaultField = (type, overrideProperties = {}) => {
    let data = {
      ...defaultFields[type],
      ...overrideProperties,
      signer: overrideProperties.signer || defaultSigner,
    };

    // force validation type for email autofillable field
    if (
      data.type === 'text'
      && data.autoFillType
      && data.autoFillType === AutofillFieldTypes.Email
    ) {
      data.validationType = 'email_address';
      // default the email field to be double the width of a text field
      // Note: This will change the field's width to 144 regardless
      // of the size of the previous text field and will keep it
      // at 144 for all text fields going forward
      data.width = DEFAULT_TEXTBOX_WIDTH * 2;
    }

    data = setDefaultSignature(data);

    if (data.signer === PREPARER && data.type === 'text') {
      data.width = DEFAULT_TEXTBOX_WIDTH;
      data.height = DEFAULT_TEXTBOX_HEIGHT;
    }

    // If they have the font_fact feature, then the default font will be different
    if (data.type === 'text' || data.type === 'date') {
      data.fontFamily = data.fontFamily || DEFAULT_FONT_FAMILY;
    }

    // change default signer to the first signer if it is not a text
    // or a checkbox field so it doesn't set the signer to sender
    // for unsupported fields
    if (data.signer === SENDER && data.type !== 'text' && data.type !== 'checkbox') {
      data.signer = signerHistory[signerHistory.length - 1];
    }

    data.placeholder = data.placeholder || '';

    // dropdown & radio button fields shouldn't be assigned to SENDER OR PREPARER
    if (
      data.type === 'dropdown'
      || data.type === 'radiobutton'
    ) {
      if (data.signer === SENDER || data.signer === PREPARER) {
        data.signer = findSignerFromHistory([SENDER, PREPARER]);

        // If there are no valid signers, a dropdown is converted to a textbox
        if (!data.signer) {
          return getDefaultField('text');
        }
      }
    }

    if (data.type === 'radiobutton') {
      // Radio buttons MUST always have a requirement to be part of a group.
      data.requirement = 'require_0-1';
    }

    // Since the first argument is an empty objects, it's safe to just return
    // the updates.
    return freeze(buildFieldUpdate({}, data));

  };

  function getPageBoundaries(pageIndex) {
    const { width: maxX, height: maxY, legacy } = pages[pageIndex];

    if (legacy === true) {
      const landscape = maxX > maxY;
      const legacy = {
        long: 11 * 80,
        short: 8.5 * 80,
      };
      return {
        maxX: landscape ? legacy.long : legacy.short,
        maxY: landscape ? legacy.short : legacy.long,
      };
    }

    return { maxX, maxY };
  }

  function repelFields(fields, address, tries = 0) {
    if (tries > 10) return address;
    let direction = 1;
    let movable = address;
    let tmp;


    for (let i = 0; i >= 0 && i < fields.length; i += direction) {
      const immovable = fields[i];
      // Don't bother testing fields on other pages
      if (
        movable.pageIndex === immovable.pageIndex
        && intersect(movable, immovable)
      ) {
        const minRight = Math.min(immovable.x + immovable.width, movable.x + movable.width);
        const minBottom = Math.min(immovable.y + immovable.height, movable.y + movable.height);

        const overlapX = minRight - Math.max(immovable.x, movable.x);
        const overlapY = minBottom - Math.max(immovable.y, movable.y);

        if (overlapX < overlapY || (tmp === 'n' || tmp === 's')) {
          if (movable.x < immovable.x || tmp === 'n') {
            tmp = 'n';
            movable = { ...movable, x: immovable.x - movable.width };
            // Only allow one change in direction to prevent infinite loops
            direction = -1;
          } else {
            tmp = 's';
            movable = { ...movable, x: immovable.x + immovable.width };
          }
        } else if (movable.y < immovable.y || tmp === 'w') {
          tmp = 'w';
          movable = { ...movable, y: immovable.y - movable.height };
          // Only allow one change in direction to prevent infinite loops
          direction = -1;
        } else {
          tmp = 'e';
          movable = { ...movable, y: immovable.y + immovable.height };
        }
      }
    }

    // If movable got replaced in that loop...
    if (movable !== address) {
      return repelFields(fields, movable, tries + 1);
    }
    return movable;
  }

  function isValidAddress(fields, address) {
    const { maxX, maxY } = getPageBoundaries(address.pageIndex);

    const page = { x: 0, y: 0, width: maxX, height: maxY };
    if (!encloses(page, address)) {
      // The field is hanging off some side of the page
      return false;
    }

    for (let i = 0; i < fields.length; i++) {
      const field = fields[i];
      if (
        field.pageIndex !== address.pageIndex
        || field.id === address.id
      ) {
        // Nothing to check if they aren't on the same page or are the same field
      } else if (encloses(field, address)) {
        debug('%j encloses %j', field, address);
        return false;
      } else if (encloses(address, field)) {
        debug('%j encloses %j', address, field);
        return false;
      }
    }

    return true;
  }

  function toValidAddress(fields, address) {
    // This isn't perfect, if you put a gap between two fields, then drag a
    // field into the gap it will overlap one of the fields. You get a similar
    // result if you leave a gap at the edge of a page and drag a field into it
    const tmp = repelFields(fields, address);

    const { maxX, maxY } = getPageBoundaries(address.pageIndex);

    // You really can't push a field off the page though
    const width = Math.min(tmp.width, maxX);
    const height = Math.min(tmp.height, maxY);
    const x = Math.min(maxX - width, Math.max(0, tmp.x));
    const y = Math.min(maxY - height, Math.max(0, tmp.y));

    if (
      x !== address.x
      || y !== address.y
      || width !== tmp.width
      || height !== tmp.height
    ) {
      return {
        ...address,
        x,
        y,
        width,
        height,
      };
    }
    return address;
  }

  function updateDefaultFields(fieldData) {
    // save default signature or initials if available for preparer
    if (fieldData.signer === PREPARER) {
      // preparer date fields are rendered using the text UI controls. It's not
      // safe to set any defaults.
      if (fieldData.type === 'date') {
        // persist the date format from the previous preparer field
        defaultFields[fieldData.type].dateFormat = fieldData.dateFormat;
        return fieldData;
      }

      if (fieldData.type === 'signature' && fieldData.signature != null) {
        defaultSignature = fieldData.signature;
      } else if (fieldData.type === 'initials' && fieldData.signature != null) {
        defaultInitials = fieldData.signature;
      }
    }

    // The next field you create will always look like the last one you
    // interacted with of the same type
    defaultFields[fieldData.type] = {
      ...defaultFields[fieldData.type],
      ...fieldData,
    };

    // Update default font face
    if (fieldData.fontFamily && (['text', 'date'].includes(fieldData.type))) {
      defaultFields[fieldData.type].fontFamily = fieldData.fontFamily;
    }

    delete defaultFields[fieldData.type].id;
    delete defaultFields[fieldData.type].invalidApiId;
    delete defaultFields[fieldData.type].value;
    delete defaultFields[fieldData.type].name;
    delete defaultFields[fieldData.type].placeholder;
    delete defaultFields[fieldData.type].group;
    delete defaultFields[fieldData.type].groupLabel;
    delete defaultFields[fieldData.type].signature;
    delete defaultFields[fieldData.type].requirement;
    delete defaultFields[fieldData.type].masked;
    delete defaultFields[fieldData.type].isEditableMergeField;
    delete defaultFields[fieldData.type].validationType;
    delete defaultFields[fieldData.type].validationCustomRegex;
    delete defaultFields[fieldData.type].validationCustomRegexFormatLabel;


    if (fieldData.type === 'dropdown') {
      defaultFields[fieldData.type].options = [];
    }
    if (fieldData.type === 'radiobutton') {
      delete defaultFields[fieldData.type].checked;
    }

    if (fieldData.autoFillType) {
      defaultFields[fieldData.type].autoFillType = null;
      defaultFields[fieldData.type].linkId = null;
      if (fieldData.autoFillType === AutofillFieldTypes.Email) {
        delete defaultFields[fieldData.type].validationType;
      }
    }

    return fieldData;
  }

  const findChanged = (before, after) => after.filter((f) => before.indexOf(f) === -1);


  const snapNewField = (fields, newField) => {
    let closestField;
    let distance;

    for (let i = 0; i < fields.length; i++) {
      const candidate = fields[i];

      if (candidate.pageIndex === newField.pageIndex
        && newField.type === 'checkbox'
        && newField.type === candidate.type
        // we don't need to check height because checkboxes are square
        && newField.width === candidate.width
      ) {
        const tmp = measureDistance(newField, candidate);
        if (distance == null || tmp < distance) {
          distance = tmp;
          closestField = candidate;
        }
      }
    }

    if (closestField != null) {
      const xOverlap = intersectX(newField, closestField);
      const yOverlap = intersectY(newField, closestField);

      if (xOverlap && yOverlap) {
        // do nothing
      } else if (xOverlap) {
        newField.x = closestField.x;
      } else if (yOverlap) {
        newField.y = closestField.y;
      }
    }

    return newField;
  };

  const keepOnPage = (fieldData) => toValidAddress([], fieldData);

  const defaultFieldTypeMap = {
    signature: intl.formatMessage(messages.fieldTypeSignature),
    initials: intl.formatMessage(messages.fieldTypeInitial),
    date: intl.formatMessage(messages.fieldTypeDateSigned),
    text: intl.formatMessage(messages.fieldTypeTextbox),
    checkbox: intl.formatMessage(messages.fieldTypeCheckbox),
    radiobutton: intl.formatMessage(messages.fieldTypeRadioItem),
    dropdown: intl.formatMessage(messages.fieldTypeDropdown),
    hyperlink: intl.formatMessage(messages.fieldTypeHyperlink),
  };

  const defaultAutofillFieldTypeMap = {
    name: intl.formatMessage(messages.fullNameLabel),
    lastName: intl.formatMessage(messages.lastNameLabel),
    firstName: intl.formatMessage(messages.firstNameLabel),
    email: intl.formatMessage(messages.emailLabel),
    company: intl.formatMessage(messages.companyLabel),
    title: intl.formatMessage(messages.titleLabel),
  };

  const generateDefaultFieldNumber = (fieldType, fields = [], isGroup = false) => {
    let maxFieldNumber = 0;
    const groupIdentifier = isGroup ? intl.formatMessage(messages.groupLabel) : '';
    const beginsWith = `${defaultFieldTypeMap[fieldType] || defaultAutofillFieldTypeMap[fieldType]}${groupIdentifier}`;
    fields.forEach((field) => {
      const currentField = isGroup ? field.groupLabel : field.name;
      const fieldTypeLength = beginsWith.length;
      if (currentField && currentField.length > 0 && currentField.indexOf(beginsWith) === 0) {
        const currentFieldNumber = parseInt(currentField.substring(fieldTypeLength).trim(), 10);
        maxFieldNumber = currentFieldNumber > maxFieldNumber ? currentFieldNumber : maxFieldNumber;
      }
    });
    return ++maxFieldNumber;
  };

  const generateDefaultFieldName = (fieldData, fields) => {
    if (fieldData.autoFillType) {
      return defaultAutofillFieldTypeMap[fieldData.autoFillType]
        + generateDefaultFieldNumber(fieldData.autoFillType, fields);
    }
    return defaultFieldTypeMap[fieldData.type] + generateDefaultFieldNumber(fieldData.type, fields);
  };

  const isDefaultFieldName = (fieldType, name, isGroup = false) => {
    if (!name) { return false; }
    const groupIdentifier = isGroup ? intl.formatMessage(messages.groupLabel) : '';
    const beginsWith = `${defaultFieldTypeMap[fieldType] || defaultAutofillFieldTypeMap[fieldType]}${groupIdentifier}`;
    const numberPart = parseInt(name.substring(beginsWith.length).trim(), 10);
    return name.indexOf(beginsWith) === 0 && isNaN(numberPart) === false;
  };

  const generateDefaultGroupFieldName = (type, fields) => {
    const groupPrefix = intl.formatMessage(
      messages.fieldTypeItemGroup,
      { fieldTypeItem: defaultFieldTypeMap[type] },
    );
    return groupPrefix + generateDefaultFieldNumber(type, fields, true);
  };

  const linkField = (fieldData, fields) => {
    if (fieldData.autoFillType) {
      const sameLink = fields.find(
        (f) => fieldData.signer === f.signer
          && f.linkId
          && f.type === fieldData.type
          && f.autoFillType
          && f.autoFillType === fieldData.autoFillType,
      );
      return { ...fieldData, linkId: sameLink ? sameLink.linkId : uuid() };
    }
    return fieldData;
  };

  const normalizeField = (fieldData, fields) => {
    let fieldName = fieldData.name;
    let groupLabel = fieldData.groupLabel;
    if (fieldData.signer !== SENDER
      && (fieldData.name == null || fieldData.name === '')
    ) {
      fieldName = generateDefaultFieldName(fieldData, fields);
    }
    if (fieldData.group && (
      (fieldData.groupLabel == null || fieldData.groupLabel === '')
      || isDefaultFieldName(fieldData.type, fieldData.name, true)
    )) {
      // If there is a group label already on the group, use that. Otherwise generate a new one
      // This should only be run for old documents the first time they're loaded
      const matchingGroups = fields.filter((f) => f.group && f.group === fieldData.group && f.groupLabel != null && f.groupLabel !== '');
      if (matchingGroups.length > 0) {
        groupLabel = matchingGroups[0].groupLabel;
      } else {
        groupLabel = generateDefaultGroupFieldName(fieldData.type, fields);
      }
    }

    let data = {
      ...defaultFields[fieldData.type],
      signer: defaultSigner,
      ...fieldData,
      ...linkField(fieldData, fields),
      id: fieldData.id || generateId(),
      name: fieldName,
      groupLabel,
    };
    // In some places signer is a number, and in other places it's a string.
    // This is the highest point in the Editor where I can just normalize
    // everything to a string.
    data.signer = String(data.signer);

    // set default signature if available
    data = setDefaultSignature(data);

    if (signerIds.indexOf(data.signer) === -1) {
      data.signer = -1;
    }

    // if there's a date format provided, overwrite it
    // this is usually coming through the field_options
    // parameter from the API if available
    if (data.type === 'date' && dateFormat !== 'default') {
      data.dateFormat = dateFormat;
    }

    data.width = data.width === 0 ? getOriginalDefaultSize(data.type, 'width') : data.width;
    data.height = data.height === 0 ? getOriginalDefaultSize(data.type, 'height') : data.height;

    return keepOnPage(data);
  };

  function createNewField(fields, fieldData) {
    if (!fieldData.type) {
      throw new Error('Missing required field: type');
    }

    trackHeapCustomEvent(`editor_added_${fieldData.type}`);
    let fieldName = fieldData.name;
    let groupFieldLabel = fieldData.groupLabel;

    if (fieldData.type === 'radiobutton') {
      // find all fields in the same group
      // if there is another with one, get that. Otherwise generate a default one.
      const existingGroupFieldLabel = fields.filter((f) => 'group' in f
        && fieldData.group
        && f.group === fieldData.group
        && f.groupLabel != null
        && f.groupLabel !== '');

      if (existingGroupFieldLabel.length > 0) {
        groupFieldLabel = existingGroupFieldLabel[0].groupLabel;
      } else {
        groupFieldLabel = generateDefaultGroupFieldName(fieldData.type, fields);
      }

      if (fieldData.name) {
        fieldName = isDefaultFieldName(fieldData.type, fieldData.name, false)
          ? null
          : fieldData.name;
      }
    }

    let newField = { ...fieldData, name: fieldName };
    if (fieldData.group) {
      newField = {
        ...newField,
        groupLabel: groupFieldLabel,
      };
    }
    newField = normalizeField(fieldData, fields);

    // When you click to create a new field a width isn't specified, so center
    // it around the mouse. When you drag a new field the width is specified
    // because it's needed for the preview.
    if (!fieldData.width) {
      newField = {
        ...newField,
        x: newField.x - newField.width / 2,
        y: newField.y - newField.height / 2,
      };

      // This prevents click-to-place from generating overlapping fields.
      newField = toValidAddress(fields, newField);
    }

    newField = snapNewField(fields, newField);

    // Since the first argument is an empty objects, it's safe to just return
    // the updates.
    newField = buildFieldUpdate({}, newField);

    return freeze(updateDefaultFields(newField));
  }

  function mergeFields(fields, updates) {
    const allIds = fields.map((f) => f.id);
    debug('mergeFields(%O, %O)', fields, updates);
    const mergedFields = fields.map((f) => {
      if (updates[f.id] != null) {
        let fieldData = keepOnPage({
          ...f,
          ...buildFieldUpdate(f, updates[f.id]),
        });
        trackHeapCustomEvent('Change Font', { 'Field Type': fieldData.type });
        fieldData = normalizeField(fieldData, fields);
        return updateDefaultFields(fieldData);
      }

      return f;
    });

    return Object.keys(updates)
      .filter((id) => allIds.indexOf(id) === -1)
      .reduce((fields, id) => {
        if (updates[id].id && allIds.indexOf(updates[id].id) !== -1) {
          // If you are updating a field the key needs to be its ID. Using an
          // unknown ID is for creating a new field.
          throw new Error('Invalid Update');
        }
        if (!updates[id].type) {
          // Don't attempt to create a field when the type wasn't specified.
          // This may be a late update to a field that was deleted.
          return fields;
        }

        const fieldData = keepOnPage(createNewField(fields, updates[id]));
        debug('new field %O', fieldData);

        return [
          ...fields,
          fieldData,
        ];
      }, mergedFields)
      .sort(fieldSorter)
      .map(freeze);
  }

  function alignFields(fields, ids, direction) {
    if (ids.length < 2) {
      return fields;
    }

    const selected = fields.filter(
      (f) => ids.indexOf(f.id) >= 0,
    );

    const boundingBox = getBoundingBox(selected);

    const updates = {};
    for (let i = 0; i < selected.length; i++) {
      switch (direction) {
        case 'left':
          updates[selected[i].id] = {
            x: boundingBox.x,
          };
          break;
        case 'right':
          updates[selected[i].id] = {
            x: boundingBox.x + boundingBox.width - selected[i].width,
          };
          break;
        case 'top':
          updates[selected[i].id] = {
            y: boundingBox.y,
          };
          break;
        case 'bottom':
          updates[selected[i].id] = {
            y: boundingBox.y + boundingBox.height - selected[i].height,
          };
          break;
        case 'center-v': {
          const bbCenter = boundingBox.x + (boundingBox.width / 2);
          updates[selected[i].id] = {
            x: bbCenter - (selected[i].width / 2),
          };
          break;
        }
        case 'center-h': {
          const bbCenter = boundingBox.y + (boundingBox.height / 2);
          updates[selected[i].id] = {
            y: bbCenter - (selected[i].height / 2),
          };
          break;
        }
        default: throw new Error(`Invalid direction: ${direction}`);
      }
    }

    const mergedFields = mergeFields(fields, updates);

    const foundIntersection = anyIntersect(mergedFields.filter(
      (f) => ids.indexOf(f.id) >= 0,
    ));

    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!foundIntersection || confirm(intl.formatMessage(messages.elementsOverlap))) {
      return mergedFields;
    }
    return fields;
  }


  const deleteFields = (fields, ids) => {
    const groupCounts = {};

    const f = fields.filter((f) => {
      // Delete the requested fields
      if (ids.includes(f.id)) {
        return false;
      }

      // If a field that isn't being deleted is in a group, count how many
      // fields are in the group.
      if (f.group != null) {
        groupCounts[f.group] = groupCounts[f.group] || [];
        groupCounts[f.group].push(f.id);
      }
      return true;
    });

    return Object.entries(groupCounts)
      // Find all groups with only one field
      .filter(([_groupName, fieldIds]) => fieldIds.length === 1)
      .reduce((f, [_groupName, [id]]) => {
        return f.filter(
          // Remove the field because it's the last member of its group
          (fieldData) => fieldData.id !== id,
        );
      }, f);
  };


  const findIntersection = (fields, a, b) => {
    const [low, high] = [a, b].sort((a, b) => (
      // I'm not using the fieldSorter because I only want to sort by page
      a.pageIndex - b.pageIndex
    ));

    const pageBoundaries = [];
    let maxWidth = 0;
    function pageMargin(pageIndex) {
      const page = pageBoundaries[pageIndex];
      return ((maxWidth - page.maxX) / 2);
    }

    for (let pageIndex = low.pageIndex; pageIndex <= high.pageIndex; pageIndex++) {
      pageBoundaries[pageIndex] = getPageBoundaries(pageIndex);
      maxWidth = Math.max(maxWidth, pageBoundaries[pageIndex].maxX);
    }

    const x1 = low.x + pageMargin(low.pageIndex);
    const x2 = high.x + pageMargin(high.pageIndex);

    // The y and height calculations have 4 different edge cases to handle
    function foo(f) {
      if (low.pageIndex === high.pageIndex) {
        // If they're on the same page, either of them might be higher on the
        // page
        return {
          y: Math.min(low.y, high.y),
          height: Math.abs(low.y - high.y),
        };
      } else if (f.pageIndex === low.pageIndex) {
        // If the selection falls off the first page, then select to the bottom
        // of that page.
        return {
          y: low.y,
          height: pageBoundaries[f.pageIndex].maxY - low.y,
        };
      } else if (f.pageIndex === high.pageIndex) {
        // If the selection ends on this page, select from the top down
        return {
          y: 0,
          height: high.y,
        };
      } else {
        // This field is on a page between the first and last pages, select the
        // whole vertical height of the page.
        return {
          y: 0,
          height: pageBoundaries[f.pageIndex].maxY,
        };
      }
    }

    return fields.filter((f) => (
      (f.pageIndex >= low.pageIndex && f.pageIndex <= high.pageIndex)
      && intersect(f, {
        pageIndex: f.pageIndex,
        x: Math.min(x1, x2) - pageMargin(f.pageIndex),
        width: Math.abs(x1 - x2),
        ...foo(f),
      })
    ));

  };

  /**
   * Determines if an IP is in the range of Private IPs
   * @param parts
   * @returns {boolean}
   */
  function isPrivateIP(parts) {
    return parts[0] === 10
      || parts[0] === 127 // Loopback address
      || (parts[0] === 172 && parts[1] >= 16 && parts[1] <= 31)
      || (parts[0] === 192 && parts[1] === 168);
  }

  /**
   * Determines if the strings looks like a valid IP
   * @param str
   * @returns {boolean}
   */
  function isValidIP(str) {
    const octet = '(25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9][0-9]?|0)';
    const regex = new RegExp(`^${octet}\\.${octet}\\.${octet}\\.${octet}$`);
    return regex.test(str);
  }

  /**
   * Determines if an IP is part of the range of public IPs
   * @param ip
   * @returns {boolean}
   */
  function isPublicIP(ip) {
    if (ip.indexOf('127.0.0.1') !== -1) {
      return false;
    }

    const path = ip.split('://')[1];
    const parts = path.split('.').map((n) => parseInt(n, 10));

    if (parts.length < 4) {
      return false;
    }

    return !isPrivateIP(parts);
  }

  function isValidUrl(url) {
    const re = /^(http|https):\/\/[^ "]+$/;
    if (!re.test(url)) {
      return false;
    }

    const path = url.split('://')[1];
    if (isValidIP(path)) {
      // If an IP address was passed, make sure it is in the Public IP range
      return isPublicIP(url);
    }


    // Make sure URL doesn't point to localhost, hellosign.com, hellofax.com
    return url.indexOf('localhost') === -1
      && url.indexOf('hellosign.com') === -1
      && url.indexOf('hellofax.com') === -1;
  }

  function validateFields(fields, siteCode = 'S') {
    const errors = [];
    /** @type {Record<string, Field[]>} * */
    const mergeFieldsByName = {};

    fields.forEach((field) => {
      if (!field.name) {
        errors.push({
          type: 'field',
          id: field.id,
          property: 'name',
          message: intl.formatMessage(messages.formFieldValidationError),
        });
      }

      if (field.validationType === 'custom_regex') {
        try {
          RegExp(field.validationCustomRegex);
        } catch (e) {
          errors.push({
            type: 'field',
            id: field.id,
            property: 'id',
            message: intl.formatMessage(messages.invalidRegex, {
              validationCustomRegex: field.validationCustomRegex,
            }),
          });
        }
      }

      // This validation is unique because it needs to run in the component that
      // updates the ID. All other validations wait until the user presses
      // "Continue".
      if (field.invalidApiId != null) {
        errors.push({
          type: 'field',
          id: field.id,
          property: 'id',
          message: intl.formatMessage(messages.duplicateApiIdValidationError),
        });
      }

      if (field.type === 'dropdown' && field.options.length === 0) {
        errors.push({
          type: 'field',
          id: field.id,
          property: 'options',
          message: intl.formatMessage(messages.noDropdownOptions),
        });
      }

      if (field.type === 'hyperlink' && !isValidUrl(field.url)) {
        errors.push({
          type: 'field',
          id: field.id,
          property: 'url',
          message: intl.formatMessage(messages.formURLFieldValidationError),
        });
      }

      if (field.signer === SENDER && field.name) {
        mergeFieldsByName[field.name] = mergeFieldsByName[field.name] || [];
        mergeFieldsByName[field.name].push(field);
      }
    });

    Object.values(mergeFieldsByName).forEach((fields) => {
      const allSameType = fields.every((f) => f.type === fields[0].type);
      if (!allSameType) {
        errors.push(...fields.map((f) => ({
          type: 'field',
          id: f.id,
          property: 'name',
          message: intl.formatMessage(messages.multiTypeMergeFieldError),
        })));
      }
    });

    if (fields.length === 0 && siteCode === 'S') {
      errors.push({
        id: null,
        message: intl.formatMessage(messages.noComponents),
      });
    }

    signers.forEach((signer) => {
      if (signer.id !== PREPARER && signer.id !== SENDER) {
        const idx = fields.findIndex((f) => f.type !== 'date' && Number(f.signer) === Number(signer.id));
        if (idx === -1) {
          if (String(signer.id) === String(currentRoleId)) {
            // When the signer is also the preparer
            // we make sure that they don't have a PREPARER field already,
            // because if they do, they have already signed the document
            // OR
            // When the signer is also the preparer and that's the only signer
            // then make sure they have at least 1 (non me-now) field assigned to them
            const preparerFields = fields.findIndex((f) => f.type !== 'date' && f.signer === PREPARER);
            if (preparerFields === -1
              || (preparerFields > -1 && signers.length === 2)) {
              errors.push({
                id: null,
                message: intl.formatMessage(messages.prepareFieldForYourself),
              });
            }

            if (signerOrder && signer.order > 0) {
              const signatureOrder = Number(signer.order) + 1;
              errors.push({
                id: null,
                message: intl.formatMessage(messages.signerOrderFields, {
                  name: PREPARER,
                  signerOrder: signatureOrder,
                }),
              });
            }
          } else {
            errors.push({
              id: null,
              message: intl.formatMessage(messages.prepareFieldForSigner, {
                name: signer.name,
              }),
            });
          }
        }

        if (hasFeature(mode, FEATURE_QES)) {
          const signatureFields = fields.findIndex((f) => f.type === 'signature' && Number(f.signer) === Number(signer.id));
          if (signatureFields === -1) {
            errors.push({
              id: null,
              message: intl.formatMessage(messages.noSignFieldForSigner, {
                name: signer.name,
              }),
            });
          }
        }
      }
    });


    return errors.length > 0 ? errors : null;
  }

  return {
    alignFields,
    createNewField,
    deleteFields,
    findChanged,
    findIntersection,
    getDefaultField,
    generateDefaultFieldName,
    generateDefaultGroupFieldName,
    isDefaultFieldName,
    mergeFields,
    normalizeField,
    sortFields: (fields) => fields.sort(fieldSorter),
    toValidAddress,
    isValidAddress,
    buildFieldUpdate,
    validateFields,
    setSigners,
    setPages,
    setDefaultSigner,
    get signer() { return defaultSigner; },
  };
}

export function makeFieldHistory(fieldName, MAX_UNDO = 100) {
  const arrUndo = [];
  const arrRedo = [];

  function undo(state) {
    if (arrUndo.length > 0) {
      arrRedo.push(state[fieldName]);

      return { [fieldName]: arrUndo.pop() };
    }
    // returning null from a st
    return null;
  }

  function redo(state) {
    if (arrRedo.length > 0) {
      arrUndo.push(state[fieldName]);
      return { [fieldName]: arrRedo.pop() };
    }
    return null;
  }

  function didUpdate(prevState, state) {
    if (prevState[fieldName] !== state[fieldName]) {
      const lastRedo = peek(arrRedo);
      const lastUndo = peek(arrUndo);

      if (prevState[fieldName] !== lastUndo && prevState[fieldName] !== lastRedo) {
        // Only push a new state if this change wasn't the result of an arrUndo/arrRedo
        arrUndo.push(prevState[fieldName]);
        // Setting an array length to 0 removes all of its items
        arrRedo.length = 0;
        while (arrUndo.length > MAX_UNDO) {
          arrUndo.shift();
        }
      }
    }
  }

  return {
    didUpdate,
    undo,
    redo,
  };

}
